import React from 'react';
import { Box, HStack, Icon, Text } from '@chakra-ui/react';
import { LuSquarePen } from 'react-icons/lu';
import BundleIconSmall from '@/icons/BundleIconSmall.tsx';

interface GeneratedByBundleNoteProps {
	/** What was generated, e.g. "Summary generated by Bundle". */
	label: string;
	/**
	 * Said instead once a user has rewritten it, e.g. "User-edited summary".
	 *
	 * The mark says who is answerable for the words being read, so it has to move the moment a person
	 * takes them over. Not sized: a one-word correction is still someone taking ownership, and any
	 * threshold would be arbitrary and impossible to explain. Absent where the caller has no way to
	 * know, which leaves the generated claim standing rather than guessing.
	 */
	editedLabel?: string;
	isEdited?: boolean;
}

/**
 * Marks content Bundle produced rather than a user — a summary, tags, a derived task — so a
 * card never presents generated text as something someone wrote. Callers own the surrounding
 * padding, since it varies with the card it sits in.
 *
 * One line either way. The edited state uses an edit mark rather than Bundle's mark, since Bundle is
 * no longer answerable for the words being read. It also takes no second person: until the read
 * supplies the editor, the person reading may not be the person who wrote them.
 */
const GeneratedByBundleNote: React.FC<GeneratedByBundleNoteProps> = ({ label, editedLabel, isEdited = false }) => {
	const showEdited = isEdited && Boolean(editedLabel);

	return (
		<HStack gap={2} align="center" color="fg.subtle">
			{showEdited ? (
				<Icon as={LuSquarePen} boxSize={3.5} aria-hidden="true" />
			) : (
				<Box as={BundleIconSmall} w={3.5} h={3} aria-hidden="true" />
			)}
			<Text textStyle="micro" lineHeight="1.5">
				{showEdited ? editedLabel : label}
			</Text>
		</HStack>
	);
};

export default GeneratedByBundleNote;
